import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Sparkles,
  Loader2,
  User,
  Target,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  Copy,
  Zap,
  Brain,
} from 'lucide-react';

const AILeadPage = () => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [useInstantMode, setUseInstantMode] = useState(true); // Mode instantané par défaut

  const exampleText = `Nom: Jean Dupont
Email: jean.dupont@email.com
Téléphone: 0692 12 34 56
Adresse: 15 rue des Palmiers, 97400 Saint-Denis
Entreprise: Dupont BTP
Type: Professionnel
Message: Je suis intéressé par une mini pelle 1.7T pour des travaux de terrassement. Budget environ 15000€.`;

  const handlePreview = async () => {
    if (!text.trim()) {
      toast.error('Veuillez coller les informations du formulaire');
      return;
    }

    setPreviewLoading(true);
    setPreviewData(null);

    try {
      const response = await aiAPI.extractLead(text);
      setPreviewData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!text.trim()) {
      toast.error('Veuillez coller les informations du formulaire');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Use instant mode (regex) or AI mode based on toggle
      const response = useInstantMode 
        ? await aiAPI.createFromLeadInstant(text)
        : await aiAPI.createFromLead(text);
      setResult(response.data);
      toast.success('Client et opportunité créés avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const fillExample = () => {
    setText(exampleText);
  };

  return (
    <div className="space-y-6" data-testid="ai-lead-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed' }}>
          <Sparkles className="w-8 h-8 text-yellow-500" />
          Création par IA
        </h1>
        <p className="text-slate-500 mt-1">
          Collez les informations d'un formulaire Facebook, email ou autre. L'IA extrait les données et crée automatiquement le client et une opportunité à qualifier.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Copy className="w-5 h-5 text-slate-400" />
              Informations du lead
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="lead-text">Collez le contenu du formulaire ou email</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fillExample}
                  className="text-yellow-600 hover:text-yellow-700"
                >
                  <Lightbulb className="w-4 h-4 mr-1" />
                  Exemple
                </Button>
              </div>
              <Textarea
                id="lead-text"
                placeholder={`Collez ici les informations du prospect...

Exemple:
Nom: Jean Dupont
Email: jean@email.com
Téléphone: 0692 XX XX XX
Message: Intéressé par une mini pelle 1.7T...`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
                data-testid="lead-text-input"
              />
            </div>

            {/* Mode Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                {useInstantMode ? (
                  <Zap className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Brain className="w-5 h-5 text-purple-500" />
                )}
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {useInstantMode ? 'Mode Instantané' : 'Mode IA Avancé'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {useInstantMode ? 'Création immédiate (< 1 sec)' : 'Analyse IA complète (10-15 sec)'}
                  </p>
                </div>
              </div>
              <Switch
                checked={!useInstantMode}
                onCheckedChange={(checked) => setUseInstantMode(!checked)}
                data-testid="mode-toggle"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handlePreview}
                disabled={previewLoading || !text.trim()}
                className="flex-1"
                data-testid="preview-btn"
              >
                {previewLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Prévisualiser (IA)
              </Button>
              <Button
                onClick={handleCreate}
                disabled={loading || !text.trim()}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                data-testid="create-from-ai-btn"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {useInstantMode ? <Zap className="w-4 h-4 mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Créer client + opportunité
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview/Result Section */}
        <div className="space-y-6">
          {/* Preview */}
          {previewData && !result && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                  <User className="w-5 h-5" />
                  Aperçu des données extraites
                  <Badge variant="outline" className="ml-auto">
                    Confiance: {Math.round((previewData.confidence || 0) * 100)}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-3 text-sm">
                  {previewData.client_name && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Nom:</dt>
                      <dd className="font-medium text-slate-800">{previewData.client_name}</dd>
                    </div>
                  )}
                  {previewData.client_email && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Email:</dt>
                      <dd className="font-medium text-slate-800">{previewData.client_email}</dd>
                    </div>
                  )}
                  {previewData.client_phone && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Téléphone:</dt>
                      <dd className="font-medium text-slate-800">{previewData.client_phone}</dd>
                    </div>
                  )}
                  {previewData.client_company && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Entreprise:</dt>
                      <dd className="font-medium text-slate-800">{previewData.client_company}</dd>
                    </div>
                  )}
                  {previewData.client_type && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Type:</dt>
                      <dd className="font-medium text-slate-800 capitalize">{previewData.client_type}</dd>
                    </div>
                  )}
                  {previewData.client_address && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Adresse:</dt>
                      <dd className="font-medium text-slate-800">{previewData.client_address}</dd>
                    </div>
                  )}
                  {previewData.machine_requested && (
                    <div className="flex justify-between border-t pt-3 mt-3">
                      <dt className="text-slate-500">Machine demandée:</dt>
                      <dd className="font-medium text-yellow-600">{previewData.machine_requested}</dd>
                    </div>
                  )}
                  {previewData.notes && (
                    <div className="border-t pt-3 mt-3">
                      <dt className="text-slate-500 mb-1">Notes:</dt>
                      <dd className="text-slate-700 bg-white p-2 rounded border">{previewData.notes}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          )}

          {/* Result */}
          {result && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-5 h-5" />
                  Création réussie !
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Client Created */}
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Client créé</span>
                  </div>
                  <p className="text-lg font-semibold text-slate-800">{result.client_name}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate(`/admin/clients/${result.client_id}`)}
                  >
                    Voir le client
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* Opportunity Created */}
                {result.opportunity_created && (
                  <div className="bg-white rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium text-yellow-800">Opportunité créée</span>
                    </div>
                    <p className="text-sm text-slate-600">Pipeline: <span className="font-medium">Nouveau Lead</span></p>
                    {result.machine_name && (
                      <p className="text-sm text-slate-600">Machine suggérée: <span className="font-medium">{result.machine_name}</span></p>
                    )}
                    <Button
                      className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                      onClick={() => navigate(`/admin/opportunities/${result.opportunity_id}`)}
                    >
                      Ouvrir l’opportunité
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {!result.opportunity_created && (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-sm text-yellow-700">
                      <strong>Note:</strong> L’opportunité n’a pas pu être créée automatiquement. Vous pouvez ouvrir le client puis créer le suivi commercial manuellement.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => navigate(`/admin/clients/${result.client_id}`)}
                    >
                      Ouvrir le client
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Confidence */}
                <div className="text-sm text-slate-500 text-center">
                  {result.parsing_method === 'instant_regex' ? (
                    <span className="flex items-center justify-center gap-1">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      Mode instantané
                      {result.tonnage_detected && <span className="ml-2">• Tonnage détecté: {result.tonnage_detected}T</span>}
                    </span>
                  ) : (
                    <span>Score de confiance IA: {Math.round((result.confidence || 0.7) * 100)}%</span>
                  )}
                </div>

                {/* Reset */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setResult(null);
                    setPreviewData(null);
                    setText('');
                  }}
                >
                  Traiter un nouveau lead
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          {!previewData && !result && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  Conseils
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-slate-600">
                <p>• Copiez-collez directement le contenu d'un formulaire Facebook Leads, d'un email ou de notes</p>
                <p>• L'IA détecte automatiquement : nom, email, téléphone, adresse, entreprise, type de client</p>
                <p>• Si une machine est mentionnée, elle sera proposée dans l’opportunité pour aider la qualification</p>
                <p>• Le suivi doit ensuite se faire dans les opportunités avant de créer un devis si besoin</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AILeadPage;
